import axios from 'axios'
import config from './config'
import logger from '../logging'
import * as signalR from "@aspnet/signalr";

export default {
    async logInAdmin(username, password) {
        let response = await axios.post(config.apiUrl + 'admin/account', {
            "email": username,
            "password": password
        });

        if (response) {
            if (response.status === 400) {
                return {
                    valid: false,
                    message: response.data
                };
            }
            if (response.status === 401) {
                return {
                    valid: false,
                    message: "Invalid username or password"
                };
            }
        }

        return {
            valid: true,
            token: response.data
        };
    },
    async validateGameCode(code) {
        let response = await axios.get(config.apiUrl + 'game-occurrence/validate/' + code);

        // 404 is treated as a valid status to indicate a game is not open yet or doesn't exist
        if (response && response.status === 404) {
            return {
                valid: false,
                message: response.data
            }
        }

        return {
            valid: true,
            game: response.data
        };
    },
    async joinGame(code, team) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/join/' + code, {
            "teamName": team.teamName,
            "teamSize": team.teamSize
        });
        
        // axios throws exceptions on 400 level errors. 400 is a designed return status code.
        if (response && response.status === 400) {
            return {
                success: false,
                message: response.data
            }
        }

        if (response && response.status === 404) {
            throw 'Invalid Game Code'
        }

        return {
            success: true,
            token: response.data.accessToken
        };
    },
    async createWebSocket(token, mode, introduceCategory, introduceQuestion, awaitingTeamAnswers,
        revealCategoryScore, revealFinalScore, teamConnected, teamJoinedGotToken, teamAnswersSubmitted, acceptingRegistrations) {
        let connection = new signalR.HubConnectionBuilder()
            .withUrl(mode === "client" ? config.clientWebSocketUrl : config.hostWebSocketUrl, {
                accessTokenFactory: () => {
                    return token;
                }
            })
            .configureLogging(signalR.LogLevel.Information)
            .build();
        connection.serverTimeoutInMilliseconds = 1000000;
        connection.on("introduceCategory", introduceCategory);
        connection.on("introduceQuestion", introduceQuestion);
        connection.on("awaitingTeamAnswers", awaitingTeamAnswers);
        connection.on("revealCategoryScore", revealCategoryScore);
        connection.on("revealCategoryScores", revealCategoryScore);
        connection.on("revealFinalScore", revealFinalScore);
        connection.on("revealFinalScores", revealFinalScore);
        connection.on("teamConnected", teamConnected);
        connection.on("teamJoinedGotToken", teamJoinedGotToken);
        connection.on("teamAnswersSubmitted", teamAnswersSubmitted);
        connection.on("acceptingRegistrations", acceptingRegistrations);

        return connection;
    },
    async connectWebSocket(connection) {
        await connection.start();
    },
    async joinGameAsHost(code, adminToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/join/' + code + "/host", {},
            createTokenHeader(adminToken));

        // axios throws exceptions on 400 level errors. 400 is a designed return status code.
        if (response && response.status === 400) {
            return {
                success: false,
                message: response.data
            }
        }

        if (response && response.status === 404) {
            throw 'Invalid Game Code'
        }

        return {
            success: true,
            token: response.data.accessToken,
            gameDetails: response.data.gameDetails
        };
    },
    async acceptRegistrations(occurrenceId, hostToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/' + occurrenceId + "/accept-registrations", {},
            createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }

        return {
            success: true
        };
    },
    async introduceCategory(occurrenceId, categoryId, hostToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/' + occurrenceId + "/introduce-category/" + categoryId, {},
            createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }

        return {
            success: true
        };
    },
    async introduceQuestion(occurrenceId, questionId, hostToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/' + occurrenceId + "/introduce-question/" + questionId, {},
            createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }

        return {
            success: true
        };
    },
    async awaitingTeamAnswers(occurrenceId, questionId, hostToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/' + occurrenceId + "/awaiting-team-answers/" + questionId, {},
            createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }

        return {
            success: true
        };
    },
    async resendQuestion(occurrenceId, questionId, teamName, hostToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/' + occurrenceId + "/resend-question/" + questionId, {teamName},
        createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }

        return {
            success: true
        };
    },
    async revealCategoryScores(occurrenceId, categoryId, hostToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/' + occurrenceId + "/reveal-category-scores/" + categoryId, {},
            createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }

        return {
            success: true
        };
    },
    async revealFinalScores(occurrenceId, hostToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/' + occurrenceId + "/reveal-final-scores", {},
            createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }

        return {
            success: true
        };
    },
    async getTeamAnswers(occurrenceId, questionId, hostToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/' + occurrenceId + "/get-team-answers/" + questionId, {},
            createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }

        return {
            success: true,
            teamAnswers: response.data
        };
    },
    async saveTeamAnswerScores(occurrenceId, teamAnswers, hostToken) {
        let response = await axios.post(config.apiUrl + 'game-occurrence/' + occurrenceId + "/update-team-answers", teamAnswers,
            createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }

        return {
            success: true
        };
    },
    async getAllTeams(occurrenceId, hostToken) {
        let response = await axios.get(config.apiUrl + 'game-occurrence/' + occurrenceId + "/get-all-teams",
            createTokenHeader(hostToken));

        if (response && response.status >= 300) {
            logger.debug(response);
            throw 'Invalid Server Response'
        }
        return response.data;
    }
}

let createTokenHeader = token => {
    return {
        headers: {
            'Authorization': "bearer " + token
        }
    };
}